// The super admin's Billing page (laravel BillingController@companies / App\BillingOverview): filtering, sorting,
// the latest invoice's state and the CSV of the table. The server sends one row per company.

export const ALL = 'all';
export const FILTERS = [
	{ id: ALL, label: 'All' },
	{ id: 'unpaid', label: 'Unpaid' },
	{ id: 'overdue', label: 'Overdue' },
	{ id: 'stopped', label: 'Suspended' },
	{ id: 'nonpayment', label: 'Suspended for non-payment' },
	{ id: 'not_billed', label: 'Not billed yet' },
];

function money(value) {
	const number = parseFloat(value);
	return isNaN(number) ? 0 : number;
}

// Whether a company row is in a filter
export function inFilter(row, filter) {
	switch (filter) {
		case 'unpaid': return row.unpaid > 0;
		case 'overdue': return row.overdue > 0;
		case 'stopped': return !!row.stopped;
		case 'nonpayment': return !!row.stopped_for_nonpayment;
		case 'not_billed': return !row.starts_on;
		default: return true;
	}
}

// The rows in the filter whose name has the search text (any case)
export function filterCompanies(rows, filter, search) {
	const text = String(search || '').trim().toLowerCase();
	return (rows || []).filter((row) => inFilter(row, filter) && (text === '' || String(row.name || '').toLowerCase().includes(text)));
}

// How many rows each filter has: { all: 12, unpaid: 3, ... }
export function filterCounts(rows) {
	const counts = {};
	FILTERS.forEach((filter) => {
		counts[filter.id] = (rows || []).filter((row) => inFilter(row, filter.id)).length;
	});
	return counts;
}

// The value a column sorts by (a company not billed yet has no figures: it sorts as nothing)
const SORT_VALUES = {
	name: (row) => String(row.name || '').toLowerCase(),
	reseller: (row) => (row.reseller ? row.reseller.toLowerCase() : null),
	starts_on: (row) => row.starts_on || null,
	rate: (row) => money(row.rate_usd),
	starts: (row) => (row.this_month ? row.this_month.duty_starts : null),
	so_far: (row) => (row.this_month ? money(row.this_month.usd) : null),
	predicted: (row) => (row.this_month ? money(row.this_month.estimate_usd) : null),
	latest: (row) => (row.latest ? row.latest.period + '|' + String(row.latest.id).padStart(10, '0') : null),
	outstanding: (row) => money(row.outstanding_usd),
	reminders: (row) => (row.latest && row.latest.reminders ? row.latest.reminders * 100000000 + Number(String(row.latest.last_reminder || '').replace(/-/g, '')) : null),
	invoiced: (row) => money(row.period.invoiced_usd),
	paid: (row) => money(row.period.paid_usd),
	period_outstanding: (row) => money(row.period.outstanding_usd),
	stopped: (row) => (row.stopped ? 1 : 0),
};

// The rows sorted by a column, ascending or descending; empty values always last, then by name
export function sortCompanies(rows, key, descending) {
	const value = SORT_VALUES[key] || SORT_VALUES.name;
	return [...(rows || [])].sort((a, b) => {
		const x = value(a);
		const y = value(b);
		if (x === null || y === null) {
			if (x !== y) {
				return x === null ? 1 : -1;
			}
		} else if (x !== y) {
			return (x < y ? -1 : 1) * (descending ? -1 : 1);
		}
		return SORT_VALUES.name(a) < SORT_VALUES.name(b) ? -1 : SORT_VALUES.name(a) > SORT_VALUES.name(b) ? 1 : a.id - b.id;
	});
}

function plural(count, one, many) {
	return count + ' ' + (count === 1 ? one : many);
}

// The latest invoice's state: { label: 'Paid' | 'Due by 8 November 2026' | 'Overdue by 4 days' | 'Void', tone }
export function latestState(latest) {
	if (!latest) {
		return { label: '', tone: 'secondary' };
	}
	if (latest.status === 'paid') {
		return { label: 'Paid', tone: 'success' };
	}
	if (latest.status === 'void') {
		return { label: 'Void', tone: 'secondary' };
	}
	if (latest.overdue_days > 0) {
		return { label: 'Overdue by ' + plural(latest.overdue_days, 'day', 'days'), tone: 'danger' };
	}
	return { label: 'Due by ' + latest.due_label, tone: 'warning' };
}

// The latest invoice's reminders: "2 sent, last 14 Nov" ('' when none were sent)
export function remindersText(latest) {
	if (!latest || !latest.reminders) {
		return '';
	}
	const last = shortDate(latest.last_reminder);
	return latest.reminders + ' sent' + (last ? ', last ' + last.replace(/ \d{4}$/, '') : '');
}

// The same, compact for the table: "2 · 14 Nov"
export function remindersShort(latest) {
	if (!latest || !latest.reminders) {
		return '';
	}
	const last = shortDate(latest.last_reminder).replace(/ \d{4}$/, '');
	return latest.reminders + (last ? ' · ' + last : '');
}

// The rows to show on one page (0-based) of size n, and how many pages there are
export function pageOf(rows, page, size) {
	const pages = Math.max(1, Math.ceil((rows || []).length / size));
	const at = Math.min(Math.max(0, page), pages - 1);
	return { rows: (rows || []).slice(at * size, at * size + size), page: at, pages };
}

function csvCell(value) {
	const text = value === null || value === undefined ? '' : String(value);
	// Quoted when needed; a leading = + - @ is kept from being read as a formula by a spreadsheet
	const safe = /^[=+\-@]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text) ? "'" + text : text;
	return /[",\r\n]/.test(safe) ? '"' + safe.replace(/"/g, '""') + '"' : safe;
}

// The table as CSV (the rows as filtered and sorted), with the chosen period's totals
export function companiesCsv(rows, periodLabel, monthLabel) {
	const header = ['Company', 'Reseller', 'Suspended', 'Billed from', 'Price per duty start (USD)', 'Some clients have their own price',
		monthLabel + ': duty starts so far', monthLabel + ': USD so far', monthLabel + ': predicted USD',
		'Latest invoice', 'Latest invoice month', 'Latest invoice USD', 'Latest invoice state', 'Outstanding USD (all)',
		periodLabel + ': invoiced USD', periodLabel + ': paid USD', periodLabel + ': outstanding USD',
		'Reminders (latest invoice)', 'Suspended for non-payment (invoice)', 'Free trial ends'];
	const lines = (rows || []).map((row) => [
		row.name, row.reseller || '', row.stopped ? 'yes' : 'no', row.starts_on || 'Not billed yet', row.rate_usd,
		row.clients_own_price ? 'yes' : 'no',
		row.this_month ? row.this_month.duty_starts : '', row.this_month ? row.this_month.usd : '', row.this_month ? row.this_month.estimate_usd : '',
		row.latest ? row.latest.number : '', row.latest ? row.latest.period : '', row.latest ? row.latest.total_usd : '',
		row.latest ? latestState(row.latest).label : '', row.outstanding_usd,
		row.period.invoiced_usd, row.period.paid_usd, row.period.outstanding_usd,
		row.latest ? remindersText(row.latest) : '', row.stopped_for_nonpayment ? row.stopped_for_nonpayment.number || 'yes' : '',
		row.trial ? row.trial.ends_on + (row.trial.active ? '' : ' (ended)') : '',
	]);
	return [header, ...lines].map((line) => line.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// Saves text as a file
export function downloadText(fileName, text, type) {
	const url = URL.createObjectURL(new Blob([text], { type: type || 'text/csv;charset=utf-8' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The period picker's choices: the months (newest first), the year so far and all time
export function periodOptions(months, monthLabel) {
	const year = (months && months[0] ? months[0] : '').slice(0, 4);
	return [
		...(months || []).map((month) => ({ value: month, label: monthLabel(month) })),
		{ value: 'year', label: year + ' so far' },
		{ value: 'all', label: 'All time' },
	];
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "1 Sep 2026" from "2026-09-01", "Sep 2026" from "2026-09" (the table's compact dates)
export function shortDate(value) {
	const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value || '');
	if (!match) {
		return value || '';
	}
	return (match[3] ? Number(match[3]) + ' ' : '') + SHORT_MONTHS[Number(match[2]) - 1] + ' ' + match[1];
}

// ---- FreeAgent (laravel App\FreeAgentSync: Martin's bookkeeping; the super admin only) ----

// The small icon after a latest invoice: { icon, tone, label (the hover), link (its page in FreeAgent or null) }; null: none
export function freeAgentMark(freeagent) {
	if (!freeagent) {
		return null;
	}
	const marks = {
		in: { icon: 'mdi-cloud-check', tone: 'success', label: 'In FreeAgent (click to open it there)' },
		waiting: { icon: 'mdi-cloud-upload', tone: 'muted', label: 'Waiting to go to FreeAgent (next sync)' },
		failed: { icon: 'mdi-alert-circle', tone: 'danger', label: 'Not in FreeAgent: ' + (freeagent.error || 'the sync failed') + ' It is tried again.' },
		cancelled: { icon: 'mdi-cloud-outline-off', tone: 'muted', label: 'Cancelled in FreeAgent (void)' },
		skipped: { icon: 'mdi-cloud-outline', tone: 'muted', label: 'Not in FreeAgent: voided before it was sent' },
		not_sent: { icon: 'mdi-cloud-outline', tone: 'muted', label: 'Not sent to FreeAgent: issued before the date FreeAgent sending starts from' }
	};
	const mark = marks[freeagent.state];
	return mark ? { ...mark, link: freeagent.url || null } : null;
}

function plainTime(iso) {
	if (!iso) {
		return '';
	}
	const date = new Date(iso);
	return isNaN(date.getTime()) ? '' : date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// The lines of the Billing page's FreeAgent box, from the server's status
export function freeAgentLines(status) {
	if (!status || !status.available) {
		return { title: 'FreeAgent', lines: ['FreeAgent is not set up on this server yet (its migration has not run).'], tone: 'muted' };
	}
	const title = 'FreeAgent (' + status.environment_label + ')';
	if (!status.configured) {
		return { title, lines: ['Not set up on this server: FREEAGENT_CLIENT_ID and FREEAGENT_CLIENT_SECRET are missing.'], tone: 'muted' };
	}
	if (!status.connected) {
		return { title, lines: ['Not connected.' + (status.last_error ? ' ' + status.last_error : '')], tone: status.last_error ? 'danger' : 'muted' };
	}
	const lines = [
		'Connected to ' + (status.company_name || status.subdomain) + (status.currency ? ' (accounts in ' + status.currency + ')' : '') + '.',
		'Last sync: ' + (status.last_sync_at ? plainTime(status.last_sync_at) : 'not yet') + '. '
			+ status.waiting + ' ' + (status.waiting === 1 ? 'invoice' : 'invoices') + ' waiting, ' + status.failed + ' failed.'
			+ (status.sync_from ? ' Invoices issued from ' + status.sync_from + '.' : '')
	];
	if (!status.sync_on) {
		lines.push('Sending is off on this server (FREEAGENT_SYNC=false): nothing is sent until it is switched on.');
	}
	if (status.paused_until) {
		lines.push('FreeAgent asked Mobiliis to slow down; sending goes on after ' + plainTime(status.paused_until) + '.');
	}
	if (status.last_error) {
		lines.push('Last error' + (status.last_error_at ? ' (' + plainTime(status.last_error_at) + ')' : '') + ': ' + status.last_error);
	}
	return { title, lines, tone: status.last_error || status.failed > 0 ? 'danger' : 'success' };
}

// What the Billing page says after coming back from FreeAgent (?freeagent=connected | failed)
export function freeAgentReturn(search) {
	const result = new URLSearchParams(search || '').get('freeagent');
	if (result === 'connected') {
		return { tone: 'success', text: 'FreeAgent is connected.' };
	}
	if (result === 'failed') {
		return { tone: 'danger', text: 'FreeAgent was not connected. The reason is in the FreeAgent box below.' };
	}
	return null;
}
